
import { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import AppShell from '@/components/layout/AppShell';
import { useAppShell } from '@/components/layout/AppShellContext';

type RoleProtectedRouteProps = {
  children: ReactNode;
  requiredRole: 'admin' | 'user' | 'instructor' | 'parent';
  bare?: boolean;
};

const RoleProtectedRoute = ({ children, requiredRole, bare }: RoleProtectedRouteProps) => {
  const { user, loading } = useAuth();
  const [hasRole, setHasRole] = useState<boolean | null>(null);
  const [isCheckingRole, setIsCheckingRole] = useState(true);
  const navigate = useNavigate();
  const { inShell } = useAppShell();

  useEffect(() => {
    const checkUserRole = async () => {
      if (!user) {
        setIsCheckingRole(false);
        return;
      }
      try {
        const { data, error } = await supabase.rpc('has_role', {
          _user_id: user.id,
          _role: requiredRole,
        });
        if (error) {
          console.error('Error checking user role:', error);
          toast.error('Error verifying permissions');
          setHasRole(false);
        } else {
          setHasRole(data);
        }
      } catch (error) {
        console.error('Error checking role:', error);
        setHasRole(false);
      } finally {
        setIsCheckingRole(false);
      }
    };

    if (!loading) {
      checkUserRole();
    }
  }, [user, loading, requiredRole]);

  useEffect(() => {
    if (!loading && !isCheckingRole && hasRole === false) {
      toast.error("You don't have permission to access this page");
      navigate('/dashboard');
    }
  }, [hasRole, isCheckingRole, loading, navigate]);

  if (loading || isCheckingRole) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Checking permissions...</span>
      </div>
    );
  }

  if (!hasRole) return null;
  return bare || inShell ? <>{children}</> : <AppShell>{children}</AppShell>;
};

export default RoleProtectedRoute;

